import { Head, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { productEditorPath, productEditorSectionPath, productPath } from "@travily/routes";
import type ListingsEditorTagline from "@travily/types/pages/ListingsEditorTagline";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { buildEditorSections } from "@/lib/editor-sections";
import { firstError } from "@/lib/forms/first-error";
import { ProductTaglineEditor } from "./show.view";

type PageProps = ListingsEditorTagline;

interface TaglineFormData {
  tagline: string;
}

const ProductEditTaglinePage = (props: PageProps) => {
  const { listing_id, tagline } = props;
  const sections = buildEditorSections(props);
  const form = useForm<TaglineFormData>({ tagline: tagline ?? "" });

  const handleSave = () => {
    form.patch(productEditorSectionPath(listing_id, "tagline"), {
      preserveScroll: "errors",
    });
  };

  return (
    <EditFrame
      activeSection="tagline"
      actions={
        <ButtonDock
          primaryAction={
            <>
              <StandardButton
                className="sm:hidden"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="56"
              >
                {t("common.actions.save")}
              </StandardButton>
              <StandardButton
                className="hidden sm:flex"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="64"
              >
                {t("common.actions.save")}
              </StandardButton>
            </>
          }
        />
      }
      backHref={productPath(listing_id)}
      href={productEditorPath(listing_id)}
      sections={sections}
      title={t("web.products.editors.show.title")}
    >
      <Head title={t("web.products.editor.sections.show.meta_title", { title: props.listing_title ?? "" })} />
      <ProductTaglineEditor
        error={firstError(form.errors.tagline as string | string[] | undefined)}
        onChange={(value) => form.setData("tagline", value)}
        value={form.data.tagline}
      />
    </EditFrame>
  );
};

ProductEditTaglinePage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default ProductEditTaglinePage;
